/**
 * Browser-side parsing and mapping for test import (task 1175).
 *
 * The service accepts JSON rows plus a column map (task 1173), so parsing a CSV
 * happens here, with `papaparse`. These helpers are pure so the mapping step,
 * the preview and the request body all read one set of rules:
 *
 *   1. A file column maps to one of the service's top-level input field keys,
 *      or to `name` / `description` / `expected`, or is skipped (absent from the
 *      map). The map is prefilled on an EXACT name match.
 *   2. A row with no non-empty mapped input value is skipped — the same rule the
 *      service applies, so the preview's counts are the ones the import reports.
 *   3. A row with no description gets `Imported from <file name>`, so provenance
 *      survives without a schema column.
 */
import Papa from 'papaparse';
import { IMPORT_TEST_COLUMNS } from '@/types/endpointTests';

export interface ParsedImportFile {
  fileName: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
}

export interface MappedImportRow {
  index: number;
  name: string;
  description: string;
  expected: string;
  input: Record<string, unknown>;
}

export interface ImportPreview {
  rows: MappedImportRow[];
  created: number;
  skipped: number;
}

/** The column a synthetic provenance description is written to when none is mapped. */
export const SYNTHETIC_DESCRIPTION_COLUMN = '__description';

/** Thrown with a User-readable message when a file cannot be read as tests. */
export class ImportFileError extends Error {}

const isEmpty = (value: unknown) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** Read a file as text through FileReader, which every target browser (and jsdom) has. */
function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new ImportFileError('This file could not be read.'));
    reader.readAsText(file);
  });
}

/** Parse a `.csv` (header row) or `.json` (array of objects) file. */
export async function parseImportFile(file: File): Promise<ParsedImportFile> {
  const text = await readFileText(file);
  const lower = file.name.toLowerCase();

  if (lower.endsWith('.json')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ImportFileError('This JSON file could not be parsed.');
    }
    if (!Array.isArray(parsed) || parsed.some((row) => !row || typeof row !== 'object' || Array.isArray(row))) {
      throw new ImportFileError('A JSON import must be an array of objects, one per scenario.');
    }
    const rows = parsed as Array<Record<string, unknown>>;
    const columns: string[] = [];
    for (const row of rows) {
      for (const key of Object.keys(row)) if (!columns.includes(key)) columns.push(key);
    }
    return { fileName: file.name, columns, rows };
  }

  if (lower.endsWith('.csv')) {
    const result = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
    const columns = (result.meta.fields ?? []).filter((field) => field !== '');
    if (columns.length === 0) {
      throw new ImportFileError('This CSV file has no header row.');
    }
    return { fileName: file.name, columns, rows: result.data };
  }

  throw new ImportFileError('Choose a .csv or .json file.');
}

/** The export's input column prefix (`input.<fieldKey>`, task 1173). */
const INPUT_COLUMN_PREFIX = 'input.';

/**
 * Prefill: a column whose name exactly equals an input field key or a test
 * column — or the export's own `input.<fieldKey>` name, so an exported file
 * imports back without remapping.
 */
export function prefillColumnMap(columns: string[], inputFieldKeys: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const column of columns) {
    const exported = column.startsWith(INPUT_COLUMN_PREFIX) ? column.slice(INPUT_COLUMN_PREFIX.length) : null;
    if (inputFieldKeys.includes(column)) map[column] = column;
    else if (exported && inputFieldKeys.includes(exported)) map[column] = exported;
    else if ((IMPORT_TEST_COLUMNS as readonly string[]).includes(column)) map[column] = column;
  }
  return map;
}

/** The service's CSV formula guard: a leading `'` before `= + - @`, tab or CR (task 1173). */
const GUARDED_CELL = /^'(?=[=+\-@\t\r])/;

/** Remove the formula guard from one exported cell. */
export const unguardCell = (value: string) => value.replace(GUARDED_CELL, '');

/**
 * The tests list as JSON in the import column shape (task 1175), built from the
 * service's CSV export: one object per test keyed by the same columns, with
 * the spreadsheet formula guard removed (it protects spreadsheets, not JSON).
 */
export function exportCsvToJson(csv: string): Array<Record<string, string>> {
  const result = Papa.parse<Record<string, string>>(csv, { header: true, skipEmptyLines: true });
  return result.data.map((row) =>
    Object.fromEntries(Object.entries(row).map(([key, value]) => [key, unguardCell(value ?? '')])),
  );
}

const provenance = (fileName: string) => `Imported from ${fileName}`;

/** Map every row through `columnMap`; rows with no mapped input value are skipped. */
export function buildImportPreview(
  parsed: ParsedImportFile,
  columnMap: Record<string, string>,
): ImportPreview {
  const testColumns = IMPORT_TEST_COLUMNS as readonly string[];
  const rows: MappedImportRow[] = [];
  let skipped = 0;

  parsed.rows.forEach((row, index) => {
    const input: Record<string, unknown> = {};
    let name = '';
    let description = '';
    let expected = '';
    for (const [column, target] of Object.entries(columnMap)) {
      const value = row[column];
      if (target === 'name') name = isEmpty(value) ? name : String(value);
      else if (target === 'description') description = isEmpty(value) ? description : String(value);
      else if (target === 'expected') expected = isEmpty(value) ? expected : String(value);
      else if (!testColumns.includes(target) && !isEmpty(value)) input[target] = value;
    }
    if (Object.keys(input).length === 0) {
      skipped += 1;
      return;
    }
    rows.push({
      index,
      name,
      description: description || provenance(parsed.fileName),
      expected,
      input,
    });
  });

  return { rows, created: rows.length, skipped };
}

/**
 * The request body's `rows` and `columnMap`: the file rows as parsed, with each
 * blank description filled with the provenance line — in the column mapped to
 * `description`, or in a synthetic column added to the map when none is.
 */
export function buildImportRequest(
  parsed: ParsedImportFile,
  columnMap: Record<string, string>,
): { columnMap: Record<string, string>; rows: Array<Record<string, unknown>> } {
  const mappedDescription = Object.keys(columnMap).find((column) => columnMap[column] === 'description');
  const descriptionColumn = mappedDescription ?? SYNTHETIC_DESCRIPTION_COLUMN;
  const map = mappedDescription ? { ...columnMap } : { ...columnMap, [descriptionColumn]: 'description' };
  const rows = parsed.rows.map((row) =>
    isEmpty(row[descriptionColumn]) ? { ...row, [descriptionColumn]: provenance(parsed.fileName) } : row,
  );
  return { columnMap: map, rows };
}
